import { getMsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime'
import { addEditorNameScope } from '../contracts/editorNameScope'
import type {
  LegacyBusyTransitionKind,
  LegacyIdleWaiter,
  LegacyLifecycleSnapshot,
  LegacyLifecycleStateListener,
  LegacyLifecycleTraceEntry,
} from './types'

class LegacyOperationGate {
  private readonly maxTraceEntries = 300;
  private queue: Promise<unknown> = Promise.resolve();
  private commandDepth = 0;
  private pendingAjax = 0;
  private activeAjaxCallbacks = 0;
  private legacyBusy = false;
  private queueDepth = 0;
  private idleCheckQueued = false;
  private installedUtils: any = null;
  private projectionSeq = 0;
  private waiters: LegacyIdleWaiter[] = [];
  private listeners = new Set<LegacyLifecycleStateListener>();
  private lastReason: string | undefined;
  private lastSettledAt: number | undefined;
  private contextStack: number[] = [];
  private traceSeq = 1;
  private traceEntries: LegacyLifecycleTraceEntry[] = [];

  run<T>(reason: string, fn: () => T): Promise<Awaited<T>> {
    this.install();
    this.queueDepth += 1;
    this.emit();

    const task = this.queue.then(async () => {
      this.queueDepth = Math.max(0, this.queueDepth - 1);
      const commandId = this.enterContext('command:start', reason);
      this.commandDepth += 1;
      this.lastReason = reason;
      this.emit();

      let result: Awaited<T>;
      try {
        result = await Promise.resolve(fn()) as Awaited<T>;
      } finally {
        this.commandDepth = Math.max(0, this.commandDepth - 1);
        this.leaveContext(commandId, 'command:end', reason);
        this.requestIdleCheck(reason);
        this.emit();
      }

      await this.whenIdle(reason);
      return result;
    });

    this.queue = task.then(
      () => undefined,
      () => undefined
    );

    return task as Promise<Awaited<T>>;
  }

  whenIdle(reason = 'manual'): Promise<void> {
    this.install();
    return new Promise(resolve => {
      this.waiters.push({ resolve });
      this.requestIdleCheck(reason);
    });
  }

  noteProjectionSync(reason: string): void {
    this.projectionSeq += 1;
    this.lastReason = reason;
    this.pushTrace('projection', reason);
    this.requestIdleCheck(reason);
    this.emit();
  }

  subscribe(listener: LegacyLifecycleStateListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): LegacyLifecycleSnapshot {
    return {
      commandDepth: this.commandDepth,
      pendingAjax: this.pendingAjax,
      activeAjaxCallbacks: this.activeAjaxCallbacks,
      queueDepth: this.queueDepth,
      projectionSeq: this.projectionSeq,
      traceDepth: this.contextStack.length,
      legacyBusy: this.legacyBusy,
      lastReason: this.lastReason,
      lastSettledAt: this.lastSettledAt,
    };
  }

  install(): boolean {
    const runtime = getMsgDeskRuntime();
    if (!runtime.browserWindow) return false;
    const utils = runtime.facade.utils;
    if (!utils) return false;
    if (!runtime.getGlobal('MSG_UTILS')) runtime.setGlobal('MSG_UTILS', utils);
    if (this.installedUtils === utils && utils.__MSG_LEGACY_OPERATION_GATE__) return true;

    this.wrapAjaxMethod(utils, 'ajax_get');
    this.wrapAjaxMethod(utils, 'ajax_post');
    utils.__MSG_LEGACY_OPERATION_GATE__ = {
      installedAt: Date.now(),
      snapshot: () => this.snapshot(),
      whenIdle: (reason?: string) => this.whenIdle(reason),
      trace: () => this.traceEntries.slice(),
      noteBusyTransition: (kind: LegacyBusyTransitionKind, reason?: string, details?: unknown) => this.noteBusyTransition(kind, reason, details),
    };
    this.installedUtils = utils;
    return true;
  }

  private wrapAjaxMethod(utils: any, method: 'ajax_get' | 'ajax_post'): void {
    const current = utils[method];
    if (typeof current !== 'function' || current.__MSG_LEGACY_OPERATION_GATE_WRAPPED__) return;

    const gate = this;
    const wrapped = function legacyOperationGateAjax(this: unknown, action: string, ajaxObj: unknown, success: unknown, ...rest: unknown[]) {
      // Tag every legacy ajax request so the PHP origin can serve Next-host-aware
      // responses (e.g. drop rendered <li> rows, keep the hidden #mcss-msges_data
      // data blob). The params bag is serialized as-is into GET or POST params,
      // depending on the legacy ajax method. Harmless until PHP branches on it.
      if (ajaxObj && typeof ajaxObj === 'object') {
        (ajaxObj as Record<string, unknown>).nextjs = 1;
        if (action === 'check_keys' || action === 'save_msg') {
          addEditorNameScope(action, ajaxObj as Record<string, unknown>, getMsgDeskRuntime().facade.editorRough());
        }
      }
      const ajaxId = gate.ajaxStart(`${method}:${action}`);
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        gate.ajaxDone(`${method}:${action}`, ajaxId);
      };

      const wrappedSuccess = typeof success === 'function'
        ? function legacyOperationGateAjaxSuccess(this: unknown, ...args: unknown[]) {
            const successId = gate.ajaxSuccessStart(`${method}:${action}`, ajaxId);
            try {
              return (success as (...cbArgs: unknown[]) => unknown).apply(this, args);
            } finally {
              gate.ajaxSuccessDone(`${method}:${action}`, successId);
              done();
            }
          }
        : success;

      try {
        return current.call(this, action, ajaxObj, wrappedSuccess, ...rest);
      } catch (error) {
        done();
        throw error;
      }
    };

    wrapped.__MSG_LEGACY_OPERATION_GATE_WRAPPED__ = true;
    wrapped.__MSG_LEGACY_OPERATION_GATE_ORIGINAL__ = current;
    utils[method] = wrapped;
  }

  private ajaxStart(reason: string): number {
    this.pendingAjax += 1;
    this.lastReason = reason;
    const id = this.pushTrace('ajax:start', reason);
    this.emit();
    return id;
  }

  private ajaxDone(reason: string, ajaxId: number): void {
    this.pendingAjax = Math.max(0, this.pendingAjax - 1);
    this.lastReason = reason;
    this.pushTrace('ajax:end', reason, ajaxId);
    this.requestIdleCheck(reason);
    this.emit();
  }

  private ajaxSuccessStart(reason: string, ajaxId: number): number {
    this.activeAjaxCallbacks += 1;
    this.lastReason = reason;
    const id = this.enterContext('ajax:success:start', reason, ajaxId);
    this.emit();
    return id;
  }

  private ajaxSuccessDone(reason: string, successId: number): void {
    this.activeAjaxCallbacks = Math.max(0, this.activeAjaxCallbacks - 1);
    this.lastReason = reason;
    this.leaveContext(successId, 'ajax:success:end', reason);
    this.requestIdleCheck(reason);
    this.emit();
  }

  private requestIdleCheck(reason: string): void {
    if (this.idleCheckQueued) return;
    this.idleCheckQueued = true;
    queueMicrotask(() => {
      this.idleCheckQueued = false;
      this.install();
      // v2 owns request serialization. Legacy busy flags are observational only:
      // some legacy editor-mode transitions leave busy=true without any active
      // ajax/callback work. Blocking the global queue on that flag stalls later
      // tabs/editor actions even though the request lifecycle is already done.
      if (this.commandDepth > 0 || this.pendingAjax > 0 || this.activeAjaxCallbacks > 0) return;

      this.lastReason = reason;
      this.lastSettledAt = Date.now();
      const waiters = this.waiters.splice(0);
      waiters.forEach(waiter => waiter.resolve());
      this.emit();
    });
  }

  private enterContext(type: LegacyLifecycleTraceEntry['type'], name: string, parentId?: number): number {
    const id = this.pushTrace(type, name, parentId);
    this.contextStack.push(id);
    return id;
  }

  private leaveContext(id: number, type: LegacyLifecycleTraceEntry['type'], name: string, popContext = true): void {
    if (popContext) {
      const index = this.contextStack.lastIndexOf(id);
      if (index >= 0) this.contextStack.splice(index, 1);
    }
    this.pushTrace(type, name, id);
  }

  private pushTrace(type: LegacyLifecycleTraceEntry['type'], name: string, parentId = this.contextStack[this.contextStack.length - 1], details?: unknown): number {
    const id = this.traceSeq++;
    this.traceEntries.push({
      id,
      parentId,
      type,
      name,
      timestamp: Date.now(),
      depth: this.contextStack.length,
      ...(details === undefined ? {} : { details }),
      counters: {
        commandDepth: this.commandDepth,
        pendingAjax: this.pendingAjax,
        activeAjaxCallbacks: this.activeAjaxCallbacks,
        legacyBusy: this.legacyBusy,
      },
    });
    if (this.traceEntries.length > this.maxTraceEntries) {
      this.traceEntries.splice(0, this.traceEntries.length - this.maxTraceEntries);
    }
    getMsgDeskRuntime().setGlobal('__MSG_LEGACY_LIFECYCLE_TRACE__', this.traceEntries);
    return id;
  }

  private emit(): void {
    const snapshot = this.snapshot();
    getMsgDeskRuntime().setGlobal('__MSG_LEGACY_OPERATION_GATE_STATE__', snapshot);
    this.listeners.forEach(listener => listener(snapshot));
  }

  noteBusyTransition(kind: LegacyBusyTransitionKind, reason?: string, details?: unknown): void {
    const traceReason = reason || kind;
    if (kind === 'busy:global-acquire') this.legacyBusy = true;
    else if (kind === 'busy:global-release') this.legacyBusy = false;
    this.lastReason = traceReason;
    this.pushTrace(kind, traceReason, undefined, details);
    this.requestIdleCheck(traceReason);
    this.emit();
  }
}

export const legacyOperationGate = new LegacyOperationGate();

export type {
  LegacyBusyTransitionKind,
  LegacyLifecycleSnapshot,
  LegacyLifecycleStateListener,
  LegacyLifecycleTraceEntry,
} from './types'
